import type { ModalProps, ScrollAreaProps } from '@mantine/core'
import { Alert, Button, Group, Modal, ScrollArea, Stack, Text } from '@mantine/core'
import type { UseFormReturnType } from '@mantine/form'
import { useDisclosure } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconAlertTriangle, IconHexagons } from '@tabler/icons-react'
import type { DefaultError, UseMutationResult, UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery } from '@tanstack/react-query'
import { dasherize } from 'inflection'
import { observer } from 'mobx-react-lite'
import type { ComponentType, RefObject } from 'react'
import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { useParams } from 'wouter'
import { navigate } from 'wouter/use-browser-location'
import type { z } from 'zod'

import type { CollapsibleContentSectionRef } from '@/components/CollapsibleContentSection'
import { LiveModuleBanner } from '@/components/LiveModuleBanner'
import { Screen } from '@/components/Screen'
import type { SectionedFormProps } from '@/components/SectionedForm'
import { SectionedForm } from '@/components/SectionedForm'
import type { RouteParams } from '@/configs/routes'
import { theme } from '@/configs/theme'
import { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useAllEsks } from '@/hooks/useAllEsks'
import { useGlobalState } from '@/hooks/useGlobalState'
import { type StepsWithValidationResult, useStepsWithValidation } from '@/hooks/useStepsWithValidation'
import { NotFoundScreen } from '@/screens/404Screen'
import { ActionsSection } from '@/screens/ContentModuleFormScreen/ActionsSection'
import { AddDataSection } from '@/screens/ContentModuleFormScreen/AddDataSection'
import { DeliveryModeSection } from '@/screens/ContentModuleFormScreen/DeliveryModeSection'
import {
  OptionalSettingsSection,
  type OptionalSettingsSectionRef,
} from '@/screens/ContentModuleFormScreen/OptionalSettingsSection'
import { RequiredSettingsSection } from '@/screens/ContentModuleFormScreen/RequiredSettingsSection'
import { StepsSection } from '@/screens/ContentModuleFormScreen/StepsSection'
import { FileProcessingError } from '@/services/api/api'
import type {
  CreateContentModuleParams,
  CreateContentModuleResponse,
  CreateFAQsParams,
  CreateStepParams,
  CreateStepResponse,
  DeleteFAQsParams,
  DeleteStepParams,
  GenerateDemographicParams,
  GenerateDemographicResponse,
  GenerateMetadataParams,
  GenerateMetadataResponse,
  GenerateObjectivesParams,
  GenerateObjectivesResponse,
  GetContentModuleResponse,
  GetContentModulesResponse,
  GetContentResponse,
  GetFAQsResponse,
  GetSidekicksResponse,
  GetSponsorTopicsResponse,
  StepRepublishingResponse,
  UpdateContentModuleParams,
  UpdateContentModuleResponse,
  UpdateContentTextParams,
  UpdateContentTextResponse,
  UpdateFAQsParams,
  UpdateResourceParams,
  UpdateResourceResponse,
  UpdateStepParams,
  UpdateStepResponse,
} from '@/services/api/api.types'
import { ContentModuleStatus } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import type { FAQItem } from '@/types/faq'
import type { MetadataFormSchema, ModuleFormSchema, StepFormSchema } from '@/utils/contentModuleForm'
import {
  castContentModuleToZodForm,
  castStepToZodForm,
  getRequiredModuleFields,
  splitMediaSteps,
  useContentModuleForm,
  useMetadataForm,
  useStepForm,
} from '@/utils/contentModuleForm'
import { getFieldSection, validateFieldsList } from '@/utils/forms'
import { getRoutePath } from '@/utils/navigation'

import { TopicSettingsSection } from './TopicSettingsSection'
import { useModuleEditLock } from './useModuleEditLock'
import { type PrimaryFileGeneration, usePrimaryFileGeneration } from './usePrimaryFileGeneration'

const LIVE_STATUSES = new Set([ContentModuleStatus.Published])

enum FormSectionId {
  TopicSettings = 'topicSettings',
  AddData = 'addData',
  CMSettings = 'requiredSettings',
  CMOptionalSettings = 'optionalSettings',
  PrimaryMediaFile = 'primaryMediaFile',
  DeliveryMode = 'deliveryMode',
  SupplementaryContent = 'supplementaryContent',
  Actions = 'actions',
}

export type CMFormSection = {
  isNew: boolean
  isLive?: boolean
  taskId?: string
  stepId?: string
  moduleForm: UseFormReturnType<z.infer<ModuleFormSchema>>
  stepForm: UseFormReturnType<z.infer<StepFormSchema>>
  metadataForm: UseFormReturnType<MetadataFormSchema>

  // FAQ State Management
  cmFAQState: {
    approved: FAQItem[]
    pending: FAQItem[]
    showValidationErrors: boolean
    isInitialized: boolean
  }
  stepFAQState: {
    approved: FAQItem[]
    pending: FAQItem[]
    showValidationErrors: boolean
    isInitialized: boolean
  }
  faqActions: {
    setCMApprovedFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setCMPendingFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setCMShowValidationErrors: (show: boolean) => void
    setCMIsInitialized: (initialized: boolean) => void
    setStepApprovedFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setStepPendingFAQs: (faqs: FAQItem[] | ((prev: FAQItem[]) => FAQItem[])) => void
    setStepShowValidationErrors: (show: boolean) => void
    setStepIsInitialized: (initialized: boolean) => void
    resetStepFAQState: () => void
  }

  openModal: (content?: ModalContent) => void
  closeModal: (afterCloseFn?: () => void) => void
  scrollToSection: (section: `${FormSectionId}`, delay?: number) => void
  scrollToField?: (fieldName: string, delay?: number) => void
  /**
   * Call after writing the module to the server outside the Save button. Clears the cached
   * draft and disarms it, so the expert is not offered work that is already saved.
   */
  markModuleSaved?: () => void
  /**
   * Whether the expert still has the "Restore Unsaved Changes" prompt open. Anything that
   * writes to the form on load has to wait for the answer, or it works from the server's
   * values while the expert's own are still sitting in the draft.
   */
  isDraftPending?: () => boolean
  setFieldRefs?: (refs: Record<string, React.RefObject<HTMLElement>>) => void
  clearValidationErrors?: () => void
  clearValidationTrigger?: number
  saveCMFAQs?: () => Promise<void>
  stepSavedTrigger?: number
  signalStepSaved?: () => void

  topicsQuery: UseQueryResult<GetSponsorTopicsResponse, DefaultError>
  sidekicksQuery: UseQueryResult<GetSidekicksResponse, DefaultError>
  sidekickPoolQuery: UseQueryResult<GetSidekicksResponse | null, DefaultError>
  contentModulesQuery: UseQueryResult<GetContentModulesResponse, DefaultError>

  cmQuery: UseQueryResult<GetContentModuleResponse, DefaultError>
  cmContentQuery: UseQueryResult<GetContentResponse, DefaultError>
  cmFAQsQuery: UseQueryResult<GetFAQsResponse, DefaultError>
  cmCreateMutation: UseMutationResult<CreateContentModuleResponse, DefaultError, CreateContentModuleParams>
  cmUpdateMutation: UseMutationResult<UpdateContentModuleResponse, DefaultError, UpdateContentModuleParams>

  stepsQuery: StepsWithValidationResult
  stepContentQuery: UseQueryResult<GetContentResponse, DefaultError>
  stepFAQsQuery: UseQueryResult<GetFAQsResponse, DefaultError>
  stepCreateMutation: UseMutationResult<CreateStepResponse, DefaultError, CreateStepParams>
  stepDeleteMutation: UseMutationResult<unknown, DefaultError, DeleteStepParams>
  stepUpdateMutation: UseMutationResult<UpdateStepResponse, DefaultError, UpdateStepParams>

  faqsCreateMutation: UseMutationResult<StepRepublishingResponse, DefaultError, CreateFAQsParams>
  faqsUpdateMutation: UseMutationResult<StepRepublishingResponse, DefaultError, UpdateFAQsParams>
  faqsDeleteMutation: UseMutationResult<StepRepublishingResponse, DefaultError, DeleteFAQsParams>

  resourceUpdateMutation: UseMutationResult<UpdateResourceResponse, DefaultError, UpdateResourceParams>

  contentUpdateMutation: UseMutationResult<UpdateContentTextResponse, DefaultError, UpdateContentTextParams>

  generateMetadataMutation: UseMutationResult<GenerateMetadataResponse, DefaultError, GenerateMetadataParams>
  generateObjectivesMutation: UseMutationResult<GenerateObjectivesResponse, DefaultError, GenerateObjectivesParams>
  generateDemographicMutation: UseMutationResult<GenerateDemographicResponse, DefaultError, GenerateDemographicParams>

  /**
   * The primary-file generation pass (decisions 37, 46 and 47). Built once by the screen
   * rather than per section: it is driven by the files and writes the module fields, which
   * live two sections apart.
   */
  primaryFileGeneration?: PrimaryFileGeneration
  /**
   * The screen's own generation instance, shared with the primary-file pass. A section that
   * writes a module field the expert isn't looking at needs this one rather than its own,
   * so the field's state is the same one the section displaying it reads.
   */
  bulkGeneration?: ReturnType<typeof useAIFieldGeneration>
}

// Only allow safe modal props to be customized to prevent overriding security settings
type CustomizableModalProps = Pick<ModalProps, 'size' | 'styles' | 'classNames'>

type ModalContent = {
  id?: number
  ModalProps?: Partial<CustomizableModalProps>
  Component: ComponentType<any>
  ComponentProps?: object
  title?: string
  subtitle?: string
}

function StatusSnapshotAlert({ onRetry }: { onRetry: () => Promise<void> }) {
  const [retrying, setRetrying] = useState(false)

  return (
    <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} mb="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
        <Text size="sm">
          File statuses couldn&rsquo;t be loaded, so the File Status column is out of date, Content Access can&rsquo;t
          be changed, and module generation is unavailable.
        </Text>
        <Button
          size="xs"
          variant="default"
          loading={retrying}
          onClick={async () => {
            setRetrying(true)
            try {
              await onRetry()
            } finally {
              setRetrying(false)
            }
          }}>
          Retry
        </Button>
      </Group>
    </Alert>
  )
}

export const ContentModuleFormScreen = observer(function ContentModuleFormScreen() {
  const params = useParams<RouteParams['contentModuleFormEdit'] | RouteParams['contentModuleFormNew']>()

  const { api, bulkUploadStore } = useGlobalState()

  const { data: moduleData } = useQuery({
    queryKey: ['module', params.id],
    queryFn: () => api.getContentModule({ task_id: params.id! }),
    enabled: !!params.id,
  })

  const {
    form: moduleForm,
    valuesVersion,
    markSaved: markModuleSaved,
    isDraftPending,
  } = useContentModuleForm({ initialData: moduleData })
  const { form: stepForm } = useStepForm()
  const metadataForm = useMetadataForm()

  // Validation error clearing mechanism
  const [clearValidationTrigger, setClearValidationTrigger] = useState(0)
  const clearValidationErrors = useCallback(() => {
    setClearValidationTrigger(prev => prev + 1)
  }, [])

  // Step saved trigger - signals when a step has been committed via "Save & Exit"
  const [stepSavedTrigger, setStepSavedTrigger] = useState(0)
  const signalStepSaved = useCallback(() => {
    setStepSavedTrigger(prev => prev + 1)
  }, [])

  // FAQ State Management
  const [cmApprovedFAQs, setCMApprovedFAQs] = useState<FAQItem[]>([])
  const [cmPendingFAQs, setCMPendingFAQs] = useState<FAQItem[]>([])
  const [cmShowValidationErrors, setCMShowValidationErrors] = useState(false)
  const [cmIsInitialized, setCMIsInitialized] = useState(false)

  const [stepApprovedFAQs, setStepApprovedFAQs] = useState<FAQItem[]>([])
  const [stepPendingFAQs, setStepPendingFAQs] = useState<FAQItem[]>([])
  const [stepShowValidationErrors, setStepShowValidationErrors] = useState(false)
  const [stepIsInitialized, setStepIsInitialized] = useState(false)

  const taskId = params.id
  const isNew = useMemo(() => !params.id, [params.id])
  useModuleEditLock(taskId, isNew, moduleForm)

  // Read from form each render so stepId updates when stageStep() sets form values
  const stepId = stepForm.getValues().stepId ?? undefined

  const [modalOpen, { open: showModal, close: hideModal }] = useDisclosure(false)
  const [modalContent, setModalContent] = useState<ModalContent[]>([])
  const modalScrollPositions = useRef<{ [key: string]: number }>({})
  const modalScrollAreaViewport = useRef<HTMLDivElement>(null)
  const modalIdCounter = useRef(0)
  const ModalScrollArea = useCallback(
    (props: ScrollAreaProps) => (
      <ScrollArea.Autosize {...props} viewportRef={modalScrollAreaViewport} type="always" scrollbarSize={16} />
    ),
    [],
  )

  const [sectionRefs] = useState(() => {
    const sectionRef = (): RefObject<CollapsibleContentSectionRef> => ({ current: undefined })
    return {
      [FormSectionId.TopicSettings]: sectionRef(),
      [FormSectionId.AddData]: sectionRef(),
      [FormSectionId.CMSettings]: sectionRef(),
      [FormSectionId.CMOptionalSettings]: sectionRef(),
      [FormSectionId.PrimaryMediaFile]: sectionRef(),
      [FormSectionId.DeliveryMode]: sectionRef(),
      [FormSectionId.SupplementaryContent]: sectionRef(),
      [FormSectionId.Actions]: sectionRef(),
    }
  })

  // Ref for OptionalSettingsSection content to access FAQ saving
  const optionalSettingsSectionRef = useRef<OptionalSettingsSectionRef>(null)

  const topicsQuery = useQuery({
    queryKey: ['sponsor', 'topics'],
    queryFn: api.getSponsorTopics,
    initialData: [],
  })

  const { sidekicksQuery, sidekickPoolQuery } = useAllEsks()

  const contentModulesQuery = useQuery({
    queryKey: ['cm', 'list'],
    queryFn: () => api.getContentModules(),
    placeholderData: [],
  })

  // Compute first available ESK for new modules
  const firstAvailableEskId = useMemo(() => {
    const sponsorEsks = sidekicksQuery.data?.eskTypes ?? []
    const poolEsks = sidekickPoolQuery.data?.eskTypes ?? []
    const sponsorEskNames = new Set(sponsorEsks.map(esk => esk.name))
    const poolEskOptions = poolEsks.filter(esk => !sponsorEskNames.has(esk.name))
    const allEsks = [...sponsorEsks, ...poolEskOptions]

    const assignedEskIds = new Set((contentModulesQuery.data ?? []).filter(cm => cm.selectEsk).map(cm => cm.selectEsk))

    const availableEsk = allEsks.find(e => !assignedEskIds.has(e.type))
    return availableEsk?.type ?? null
  }, [sidekicksQuery.data, sidekickPoolQuery.data, contentModulesQuery.data])

  const cmQuery = useQuery({
    queryKey: ['cm', taskId],
    queryFn: () => api.getContentModule({ task_id: taskId! }),
    enabled: !isNew,
    gcTime: 0,
    retry: false,
  })

  const isLive = !!cmQuery.data?.status && LIVE_STATUSES.has(cmQuery.data.status as ContentModuleStatus)

  const invalidateStepsIfRepublishing = (data?: { stepRepublishing?: boolean }) => {
    if (data?.stepRepublishing) {
      api.queryClient.invalidateQueries({ queryKey: ['steps', taskId] })
    }
  }

  const additionalEndpointsEnabled = useMemo(() => {
    return !isNew && cmQuery.isSuccess && cmQuery.data.editable !== false
  }, [isNew, cmQuery.data, cmQuery.isSuccess])

  const cmContentQuery = useQuery({
    queryKey: ['cm', 'content', taskId],
    queryFn: () => api.getContent({ task_id: taskId! }),
    enabled: additionalEndpointsEnabled,
  })

  const cmFAQsQuery = useQuery({
    queryKey: ['cm', 'faqs', taskId],
    queryFn: () => api.getFAQs({ task_id: taskId! }),
    enabled: additionalEndpointsEnabled,
  })

  const baseStepsQuery = useQuery({
    queryKey: ['steps', taskId],
    queryFn: () => api.getSteps({ task_id: taskId! }),
    enabled: !!taskId && !isNew,
    gcTime: 0,
    notifyOnChangeProps: ['data', 'error', 'isSuccess', 'isLoading'] as const,
    refetchInterval: query => {
      const steps = query.state.data
      if (steps?.some(s => s.inProcess === true)) return 5000
      return false
    },
  })

  const stepsQuery = useStepsWithValidation(baseStepsQuery, taskId)

  const stepContentQuery = useQuery({
    queryKey: ['step', 'content', stepId],
    queryFn: () => api.getContent({ step_id: stepId! }),
    enabled: !!stepId,
  })

  const stepFAQsQuery = useQuery({
    queryKey: ['step', 'faqs', stepId],
    queryFn: () => api.getFAQs({ step_id: stepId! }),
    enabled: !!stepId,
  })

  const cmCreateMutation = useMutation({
    mutationFn: api.createContentModule,
    onSuccess: data => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })

      navigate(getRoutePath('contentModuleFormEdit', { id: data.taskId }), { replace: true })

      notifications.show({
        title: 'Module Created',
        message: 'The module was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      sectionRefs.requiredSettings.current?.open()
      sectionRefs.optionalSettings.current?.open()
      sectionRefs.primaryMediaFile.current?.open()
      sectionRefs.supplementaryContent.current?.open()
    },
    onError: (error: Error) => {
      const isProcessingError = error instanceof FileProcessingError
      notifications.show({
        title: isProcessingError ? 'File processing failed' : 'Something went wrong.',
        message: isProcessingError
          ? 'There was an issue processing your file. This may be due to the file being too large or in an unsupported format. Please try a smaller file or contact support.'
          : 'There was an issue creating the module. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const cmUpdateMutation = useMutation({
    mutationFn: api.updateContentModule,
    onSuccess: () => {
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
    },
    // Save is the one action with nothing else to fall back on: it neither navigates nor
    // clears, so without this a failure reads as the button simply having stopped.
    //
    // Not for the primary swap, which is the only caller that sends `primary_step_id` and
    // reports its own failure in the swap's own terms. Both firing told the expert their
    // module save had failed when no save had been attempted and none of their unsaved edits
    // had been sent.
    onError: (_error, variables) => {
      if (variables.primary_step_id) return
      notifications.show({
        title: 'Could Not Save Module',
        message: 'There was an issue saving your changes. Please try again.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const stepUpdateMutation = useMutation({
    mutationFn: api.updateStep,
    onSuccess: (data, variables) => {
      stepsQuery.refetch({ stepId: variables.step_id })
      clearValidationErrors()
      invalidateStepsIfRepublishing(data)
    },
    onError: () => undefined,
  })

  const contentUpdateMutation = useMutation({
    mutationFn: api.updateContentText,
    onSuccess: data => {
      cmContentQuery.refetch()
      if (stepId) stepContentQuery.refetch()
      clearValidationErrors()
      invalidateStepsIfRepublishing(data)
    },
    onError: (error: Error) => {
      if (error instanceof FileProcessingError) {
        notifications.show({
          title: 'File processing failed',
          message:
            'There was an issue processing your file. This may be due to the file being too large or in an unsupported format. Please try a smaller file or contact support.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
  })

  const faqsCreateMutation = useMutation({
    mutationFn: api.createFAQs,
    onSuccess: data => {
      cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
      invalidateStepsIfRepublishing(data)
    },
    onError: () => undefined,
  })

  const faqsUpdateMutation = useMutation({
    mutationFn: api.updateFAQs,
    onSuccess: data => {
      if (!stepId) cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
      invalidateStepsIfRepublishing(data)
    },
    onError: () => undefined,
  })

  const faqsDeleteMutation = useMutation({
    mutationFn: api.deleteFAQs,
    onSuccess: data => {
      cmFAQsQuery.refetch()
      if (stepId) stepFAQsQuery.refetch()
      invalidateStepsIfRepublishing(data)
    },
    onError: () => undefined,
  })

  const resourceUpdateMutation = useMutation({
    mutationFn: api.updateResource,
    onSuccess: async () => {
      if (stepId) {
        await api.queryClient.invalidateQueries({ queryKey: ['step', 'resources', 'all', stepId] })
        await stepsQuery.refetch({ stepId })
      }
    },
    onError: (error: Error) => {
      if (error instanceof FileProcessingError) {
        notifications.show({
          title: 'File processing failed',
          message:
            'There was an issue processing your file. This may be due to the file being too large or in an unsupported format. Please try a smaller file or contact support.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
  })

  const stepCreateMutation = useMutation({
    mutationFn: (params: CreateStepParams) =>
      api.createStep(params, undefined, { afterLastStep: bulkUploadStore.jobHoldsModule(params.task_id) }),
    onSuccess: async data => {
      notifications.show({
        title: 'File Created',
        message: 'The file was successfully created.',
        color: 'green',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })

      const steps = await stepsQuery.refetch()

      const step = steps.data?.find(step => step.stepId === data.stepId)

      // A settle landing mid-request cancels this refetch silently — it resolves with the
      // pre-create list instead of rejecting — so a miss is not evidence the step is
      // absent. Seed from the create response, which carries what a new step has.
      stepForm.setValues(
        castStepToZodForm(
          step ?? { stepId: data.stepId, name: data.generated?.name, introduction: data.generated?.introduction },
        ),
      )

      clearValidationErrors()
      invalidateStepsIfRepublishing(data)
    },
    onError: () => {
      notifications.show({
        title: 'Something went wrong.',
        message: 'There was an issue creating the file. Please try again or contact support.',
        color: 'red',
        classNames: commonClasses.notificationFilled,
        position: 'bottom-left',
      })
    },
  })

  const stepDeleteMutation = useMutation({
    mutationFn: api.deleteStep,
    onSuccess: (_data, { showToast = true }) => {
      api.queryClient.invalidateQueries({ queryKey: ['steps', taskId] })

      if (showToast) {
        notifications.show({
          title: 'File Deleted',
          message: 'The file was successfully deleted.',
          color: 'green',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })
      }
    },
    onError: () => undefined,
  })

  const generateMetadataMutation = useMutation({
    mutationFn: api.generateMetadata,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('module', undefined)
      } else if ('step_id' in vars) {
        metadataForm.setFieldValue('step', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('module', data.generated[0])
      } else if ('step_id' in vars) {
        metadataForm.setFieldValue('step', data.generated[0])
      }
    },
    onError: () => undefined,
  })

  const generateDemographicMutation = useMutation({
    mutationFn: api.generateDemographic,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('demographic', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('demographic', data.demographic)
      }
    },
    onError: () => undefined,
  })

  const generateObjectivesMutation = useMutation({
    mutationFn: api.generateObjectives,
    onMutate: vars => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('sectionObjectives', undefined)
      }
    },
    onSuccess: (data, vars) => {
      if ('task_id' in vars) {
        metadataForm.setFieldValue('sectionObjectives', data.objectives)
      }
    },
    onError: () => undefined,
  })

  const initializeModuleForm = useEffectEvent((formData: z.infer<ModuleFormSchema>) => {
    moduleForm.initialize(formData)
    // `initialize` is one-shot but the effect below runs on every refetch, and `resetDirty`
    // adopts whatever is on screen as the new baseline. Only safe when there is nothing
    // to protect: a refetch mid-edit (changing the primary file does one) would otherwise
    // read an unsaved edit as clean and drop the leave-guard with it. Skipping it while
    // dirty also keeps the delivery-mode default suppressed, since that seeds a value the
    // response does not carry.
    if (!moduleForm.isDirty()) moduleForm.resetDirty()
  })

  useEffect(() => {
    if (!cmQuery.isSuccess) return

    const formData = castContentModuleToZodForm(cmQuery.data)

    // Default to first available ESK for new modules without an ESK assigned
    if (!formData.selectEsk && firstAvailableEskId) {
      formData.selectEsk = firstAvailableEskId
    }

    initializeModuleForm(formData)
  }, [cmQuery.data, cmQuery.isFetched, cmQuery.isSuccess, firstAvailableEskId])

  const openModal = useCallback(
    (content?: ModalContent) => {
      if (content) {
        const id = ++modalIdCounter.current
        setModalContent(prevState => {
          const currentModal = prevState[0]

          if (currentModal) {
            modalScrollPositions.current[dasherize(currentModal.title ?? '')] =
              modalScrollAreaViewport.current?.scrollTop ?? 0
          }

          return [{ ...content, id }, ...prevState]
        })

        setTimeout(() => modalScrollAreaViewport.current?.scrollTo({ top: 0, behavior: 'instant' }), 50)

        showModal()
      }
    },
    [showModal],
  )

  const closeModal: CMFormSection['closeModal'] = useCallback(
    afterCloseFn => {
      const shouldCompletelyClose = modalContent.length === 1

      if (shouldCompletelyClose) {
        hideModal()
        modalScrollPositions.current = {}
      }
      setTimeout(
        () => {
          let nextModal: ModalContent | undefined

          setModalContent(prevState => {
            const [_current, ...rest] = prevState
            nextModal = rest[0]
            return rest
          })

          afterCloseFn?.()

          const nextModalPosY = modalScrollPositions.current[dasherize(nextModal?.title ?? '')] ?? 0

          setTimeout(() => {
            modalScrollAreaViewport.current?.scrollTo({ top: nextModalPosY, behavior: 'instant' })
          }, 50)
        },
        shouldCompletelyClose ? 300 : 0,
      )
    },
    [hideModal, modalContent],
  )

  // Store field refs from sections
  const fieldRefsStore = useRef<Record<string, React.RefObject<HTMLElement>>>({})

  const scrollToSection = useCallback(
    (section: `${FormSectionId}`, delay?: number) => {
      setTimeout(() => sectionRefs[section].current?.scrollIntoView(), delay ?? 0)
    },
    [sectionRefs],
  )

  // Falls back to the field's own section when nothing registered a ref for it. A field
  // that moves between sections otherwise leaves "Scroll to Error" doing nothing at all,
  // which is how the Delivery Mode move went unnoticed — a section scroll is worse than a
  // focused input and far better than a button that silently does nothing.
  const scrollToField = useCallback(
    (fieldName: string, delay?: number) => {
      setTimeout(() => {
        const fieldRef = fieldRefsStore.current[fieldName]
        if (fieldRef?.current) {
          fieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
          fieldRef.current.focus()
          return
        }
        sectionRefs[getFieldSection(fieldName)]?.current?.scrollIntoView()
      }, delay ?? 0)
    },
    [sectionRefs],
  )

  const setFieldRefs = useCallback((refs: Record<string, React.RefObject<HTMLElement>>) => {
    fieldRefsStore.current = { ...fieldRefsStore.current, ...refs }
  }, [])

  const commonData = useMemo(
    () => ({ taskId, isNew, isLive, stepId, clearValidationTrigger, stepSavedTrigger }),
    [taskId, isNew, isLive, stepId, clearValidationTrigger, stepSavedTrigger],
  )
  const forms = useMemo(
    () => ({ moduleForm, stepForm, metadataForm, markModuleSaved, isDraftPending }),
    [moduleForm, stepForm, metadataForm, markModuleSaved, isDraftPending],
  )
  const controls = useMemo(
    () => ({
      openModal,
      closeModal,
      scrollToSection,
      scrollToField,
      setFieldRefs,
      clearValidationErrors,
      signalStepSaved,
    }),
    [openModal, closeModal, scrollToSection, scrollToField, setFieldRefs, clearValidationErrors, signalStepSaved],
  )

  // FAQ state and actions
  const faqState = useMemo(
    () => ({
      cmFAQState: {
        approved: cmApprovedFAQs,
        pending: cmPendingFAQs,
        showValidationErrors: cmShowValidationErrors,
        isInitialized: cmIsInitialized,
      },
      stepFAQState: {
        approved: stepApprovedFAQs,
        pending: stepPendingFAQs,
        showValidationErrors: stepShowValidationErrors,
        isInitialized: stepIsInitialized,
      },
      faqActions: {
        setCMApprovedFAQs,
        setCMPendingFAQs,
        setCMShowValidationErrors,
        setCMIsInitialized,
        setStepApprovedFAQs,
        setStepPendingFAQs,
        setStepShowValidationErrors,
        setStepIsInitialized,
        resetStepFAQState: () => {
          setStepApprovedFAQs([])
          setStepPendingFAQs([])
          setStepShowValidationErrors(false)
          setStepIsInitialized(false)
        },
      },
    }),
    [
      cmApprovedFAQs,
      cmPendingFAQs,
      cmShowValidationErrors,
      cmIsInitialized,
      stepApprovedFAQs,
      stepPendingFAQs,
      stepShowValidationErrors,
      stepIsInitialized,
    ],
  )
  const operations = useMemo(
    () => ({
      cmQuery,
      cmContentQuery,
      cmFAQsQuery,
      sidekicksQuery,
      sidekickPoolQuery,
      contentModulesQuery,
      stepsQuery,
      cmCreateMutation,
      cmUpdateMutation,
      contentUpdateMutation,
      faqsCreateMutation,
      faqsUpdateMutation,
      faqsDeleteMutation,
      resourceUpdateMutation,
      stepCreateMutation,
      stepDeleteMutation,
      stepFAQsQuery,
      stepUpdateMutation,
      stepContentQuery,
      generateMetadataMutation,
      generateObjectivesMutation,
      generateDemographicMutation,
      topicsQuery,
      saveCMFAQs: async () => {
        await optionalSettingsSectionRef.current?.saveFAQs()
      },
    }),
    [
      cmQuery,
      cmContentQuery,
      cmFAQsQuery,
      sidekicksQuery,
      sidekickPoolQuery,
      contentModulesQuery,
      stepsQuery,
      cmCreateMutation,
      cmUpdateMutation,
      contentUpdateMutation,
      faqsCreateMutation,
      faqsUpdateMutation,
      faqsDeleteMutation,
      resourceUpdateMutation,
      stepCreateMutation,
      stepDeleteMutation,
      stepFAQsQuery,
      stepUpdateMutation,
      stepContentQuery,
      generateMetadataMutation,
      generateObjectivesMutation,
      generateDemographicMutation,
      topicsQuery,
    ],
  )

  // Separated from sections so moduleForm isn't a direct dep of the sections memo.
  const sectionValidation = useMemo(() => {
    const required = getRequiredModuleFields()
    const presentationValid = validateFieldsList(moduleForm, required.presentation).isValid
    const steps = stepsQuery.data ?? []
    const { primary: primaryStep, supplementary: supplementarySteps } = splitMediaSteps(
      steps,
      cmQuery.data?.primaryStepId,
    )

    return {
      topicSettings: validateFieldsList(moduleForm, required.topicSettings).isValid,
      primaryMediaFile: presentationValid && !!primaryStep && primaryStep.valid,
      // Supplementary is optional; only present items need to be valid.
      supplementaryContent: supplementarySteps.every(step => step.valid),
      requiredSettings: validateFieldsList(moduleForm, required.requiredSettings).isValid,
      deliveryMode: validateFieldsList(moduleForm, required.deliveryMode).isValid,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useForm returns a new object every render; valuesVersion and errors are what the validation reads change with
  }, [valuesVersion, moduleForm.errors, stepsQuery.data, cmQuery.data?.primaryStepId])

  // One generation instance for the bulk pass, held here because it is driven by the files
  // and the fields it writes live two sections further down.
  const bulkGeneration = useAIFieldGeneration({ ...commonData, ...controls, ...forms, ...operations, ...faqState })
  const primaryFileGeneration = usePrimaryFileGeneration(
    { ...commonData, ...controls, ...forms, ...operations, ...faqState },
    bulkGeneration,
  )

  const sections = useMemo(
    () =>
      [
        {
          id: FormSectionId.TopicSettings,
          sectionRef: sectionRefs.topicSettings,
          collapsible: false,
          defaultCollapsed: false,
          title: 'Select Sidekick',
          subtitle: undefined,
          ContentElement: (
            <TopicSettingsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />
          ),
          valid: sectionValidation.topicSettings,
        },
        {
          id: FormSectionId.AddData,
          sectionRef: sectionRefs.addData,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Add Primary Content',
          subtitle: 'Upload the primary content file for this module or choose to enter your content manually.',
          ContentElement: <AddDataSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />,
          valid: !!cmContentQuery.data,
          hidden: true,
        },
        {
          id: FormSectionId.PrimaryMediaFile,
          sectionRef: sectionRefs.primaryMediaFile,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Primary File',
          ContentElement: (
            <StepsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} mode="primary" />
          ),
          valid: sectionValidation.primaryMediaFile,
        },
        {
          id: FormSectionId.DeliveryMode,
          sectionRef: sectionRefs.deliveryMode,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Choose Delivery Mode',
          subtitle: 'How the Sidekick presents this module to members.',
          // Decision 9 gates this step on the primary the same way the module fields are.
          hidden: !splitMediaSteps(stepsQuery.data, cmQuery.data?.primaryStepId).primary,
          ContentElement: (
            <DeliveryModeSection {...commonData} {...controls} {...forms} {...operations} {...faqState} />
          ),
          valid: sectionValidation.deliveryMode,
        },
        {
          id: FormSectionId.SupplementaryContent,
          sectionRef: sectionRefs.supplementaryContent,
          collapsible: !isNew,
          defaultCollapsed: false,
          title: 'Supplementary Content',
          ContentElement: (
            <StepsSection {...commonData} {...controls} {...forms} {...operations} {...faqState} mode="supplementary" />
          ),
          valid: sectionValidation.supplementaryContent,
        },
        {
          id: FormSectionId.CMSettings,
          sectionRef: sectionRefs.requiredSettings,
          collapsible: !isNew,
          defaultCollapsed: isNew,
          title: 'Describe Module',
          subtitle: undefined,
          ContentElement: (
            <RequiredSettingsSection
              {...commonData}
              {...controls}
              {...forms}
              {...operations}
              {...faqState}
              bulkGeneration={bulkGeneration}
            />
          ),
          valid: sectionValidation.requiredSettings,
        },
        {
          id: FormSectionId.CMOptionalSettings,
          sectionRef: sectionRefs.optionalSettings,
          hidden: true,
          collapsible: !isNew,
          defaultCollapsed: isNew,
          title: 'Add Module FAQs',
          subtitle: 'Submit optional information for the module.',
          ContentElement: (
            <OptionalSettingsSection
              {...commonData}
              {...controls}
              {...forms}
              {...operations}
              {...faqState}
              ref={optionalSettingsSectionRef}
            />
          ),
        },
        {
          id: FormSectionId.Actions,
          sectionRef: sectionRefs.actions,
          hidden: isNew,
          collapsible: false,
          ContentElement: (
            <ActionsSection
              {...commonData}
              {...controls}
              {...forms}
              {...operations}
              {...faqState}
              primaryFileGeneration={primaryFileGeneration}
            />
          ),
        },
      ] satisfies SectionedFormProps['sections'],
    [
      isNew,
      sectionRefs,
      sectionValidation,
      stepsQuery.data,
      cmContentQuery.data,
      cmQuery.data?.primaryStepId,
      commonData,
      controls,
      forms,
      operations,
      faqState,
      primaryFileGeneration,
      bulkGeneration,
    ],
  )

  if (!isNew && (cmQuery.isError || (cmQuery.isSuccess && cmQuery.data.editable === false))) {
    return <NotFoundScreen />
  }
  return (
    <Screen
      title={isNew ? 'New Module' : ['Edit Module', cmQuery.data?.title].filter(Boolean).join(': ')}
      icon={IconHexagons}>
      {isLive && (
        <LiveModuleBanner message="You are editing a live module. Changes saved here will be published immediately." />
      )}
      {stepsQuery.isStepsError && !stepsQuery.data.length && (
        <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />} mb="md">
          This module&rsquo;s files couldn&rsquo;t be loaded. The sections below are empty because of that, not because
          the module has none — reload the page before adding anything.
        </Alert>
      )}
      {!(stepsQuery.isStepsError && !stepsQuery.data.length) && stepsQuery.isStatusError && (
        <StatusSnapshotAlert onRetry={stepsQuery.retryStatuses} />
      )}
      <SectionedForm sections={sections} />

      <Modal
        opened={modalOpen}
        centered
        trapFocus
        radius="md"
        padding="lg"
        closeOnEscape={false}
        closeOnClickOutside={false}
        withCloseButton={false}
        size={modalContent[0]?.ModalProps?.size ?? theme.rem(1200)}
        onClose={closeModal}
        transitionProps={{ duration: 300 }}
        scrollAreaComponent={ModalScrollArea}
        styles={modalContent[0]?.ModalProps?.styles}
        classNames={{
          ...modalContent[0]?.ModalProps?.classNames,
          body: commonClasses.stickyModalFooterBody,
        }}
        title={
          modalContent[0]?.title && (
            <Stack gap={0}>
              <Text size="xl" fw={600}>
                {modalContent[0]?.title}
              </Text>

              {!!modalContent[0]?.subtitle && (
                <Text size="md" c="dimmed">
                  {modalContent[0].subtitle}
                </Text>
              )}
            </Stack>
          )
        }>
        {modalContent.map((content, index) => {
          const Component = content.Component
          return (
            <div key={content.id} style={index === 0 ? undefined : { display: 'none' }}>
              <Component
                {...commonData}
                {...controls}
                {...forms}
                {...operations}
                {...faqState}
                {...content.ComponentProps}
              />
            </div>
          )
        })}
      </Modal>
    </Screen>
  )
})
